import { Link } from "react-router-dom";
import { ArrowRight, Clock, FileText, TrendingUp, Shield, Zap } from "lucide-react";
import { Button } from "../components/ui/button";

// Brand colors from logo
const BRAND_NAVY = "#1e3a5f";
const BRAND_TEAL = "#2dd4bf";

const Landing = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-md z-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center min-w-0">
            <img 
              src="https://customer-assets.emergentagent.com/job_4aa98161-0d7b-4cea-a5c7-31bfb2b47aa3/artifacts/f6ktx9y0_billoralaw_brand_color.png" 
              alt="BilloraLaw" 
              className="h-11 sm:h-14 object-contain"
            />
          </div>
          <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
            <Link to="/portal/login">
              <Button 
                variant="outline"
                className="rounded-full px-3 sm:px-5 text-xs sm:text-sm font-medium border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900 h-9 sm:h-10"
                data-testid="landing-client-access-btn"
              >
                <span className="hidden sm:inline">Acceso Clientes</span>
                <span className="sm:hidden">Clientes</span>
              </Button>
            </Link>
            <Link to="/login">
              <Button 
                className="text-white rounded-full px-3 sm:px-5 text-xs sm:text-sm font-medium hover:opacity-90 h-9 sm:h-10"
                style={{ backgroundColor: BRAND_NAVY }}
                data-testid="landing-lawyer-access-btn"
              >
                <span className="hidden sm:inline">Acceso Abogados</span>
                <span className="sm:hidden">Abogados</span>
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section with App Preview */}
      <section className="pt-32 pb-20 relative overflow-hidden min-h-[85vh] flex items-center">
        {/* Subtle gradient background */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            background: `linear-gradient(135deg, white 0%, white 50%, ${BRAND_TEAL}08 100%)`
          }}
        ></div>
        
        {/* Decorative blur elements */}
        <div 
          className="absolute top-20 right-1/4 w-[500px] h-[500px] rounded-full blur-3xl opacity-10"
          style={{ backgroundColor: BRAND_TEAL }}
        ></div>
        
        <div className="w-full relative z-10">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-4 items-center">
              {/* Left Content */}
              <div className="max-w-xl">
                <h1 
                  className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight"
                  style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}
                >
                  <span style={{ color: BRAND_NAVY }}>Gestión legal</span><br />
                  <span style={{ color: BRAND_TEAL }}>sin fricciones.</span>
                </h1>
                <p className="mt-6 text-lg text-slate-600 max-w-md">
                  La plataforma esencial para estudios jurídicos modernos. Control de horas, facturación y reportes en una sola interfaz.
                </p>
                <div className="mt-8">
                  <Link to="/login">
                    <Button 
                      className="text-white rounded-full px-8 py-6 text-lg font-medium hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: BRAND_NAVY }}
                    >
                      Acceso al Portal
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                </div>
                <div className="mt-8 flex items-center gap-6 text-sm">
                  <span className="flex items-center gap-2 text-slate-500">
                    <Shield className="h-4 w-4" style={{ color: BRAND_TEAL }} />
                    SEGURIDAD
                  </span>
                  <span className="flex items-center gap-2 text-slate-500">
                    <Zap className="h-4 w-4" style={{ color: BRAND_TEAL }} />
                    EFICIENCIA
                  </span>
                </div>
              </div>

              {/* Right Content - App Preview Image - Justified Right */}
              <div className="relative hidden lg:block">
                {/* Empty spacer for grid alignment */}
              </div>
            </div>
          </div>
          
          {/* App Preview Image - Positioned absolute to right edge */}
          <div 
            className="absolute top-1/2 -translate-y-1/2 right-0 hidden lg:block"
            style={{ 
              width: '40%',
              maxWidth: '630px'
            }}
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_4aa98161-0d7b-4cea-a5c7-31bfb2b47aa3/artifacts/zjjvjt89_pantallas_billora.png"
              alt="BilloraLaw App Preview"
              className="w-full h-auto object-contain object-right"
              style={{ 
                maxHeight: '455px',
                filter: 'drop-shadow(0 25px 50px rgba(0, 0, 0, 0.15))'
              }}
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-slate-50/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="text-left">
              <div 
                className="h-12 w-12 rounded-xl flex items-center justify-center mb-4"
                style={{ backgroundColor: `${BRAND_NAVY}15` }}
              >
                <Clock className="h-6 w-6" style={{ color: BRAND_NAVY }} />
              </div>
              <h3 className="text-lg font-semibold mb-2" style={{ color: BRAND_NAVY }}>Control de Horas</h3>
              <p className="text-slate-600">
                Registro preciso para una facturación transparente y eficiente de cada caso.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="text-left">
              <div 
                className="h-12 w-12 rounded-xl flex items-center justify-center mb-4"
                style={{ backgroundColor: `${BRAND_TEAL}20` }}
              >
                <FileText className="h-6 w-6" style={{ color: BRAND_TEAL }} />
              </div>
              <h3 className="text-lg font-semibold mb-2" style={{ color: BRAND_NAVY }}>Facturación</h3>
              <p className="text-slate-600">
                Automatización de liquidaciones y reportes financieros para su firma.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="text-left">
              <div 
                className="h-12 w-12 rounded-xl flex items-center justify-center mb-4"
                style={{ backgroundColor: `${BRAND_NAVY}15` }}
              >
                <TrendingUp className="h-6 w-6" style={{ color: BRAND_NAVY }} />
              </div>
              <h3 className="text-lg font-semibold mb-2" style={{ color: BRAND_NAVY }}>Rendimiento</h3>
              <p className="text-slate-600">
                Analice la rentabilidad de su equipo y procesos en tiempo real.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 pb-16 px-6 border-t border-slate-100">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-slate-600">
            <span className="font-semibold" style={{ color: BRAND_NAVY }}>BilloraLaw</span>
            <span className="text-slate-300">|</span>
            <span>2026</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-slate-500">
            <a href="#" className="hover:text-slate-900 transition-colors">Soporte</a>
            <a href="/privacy" className="hover:text-slate-900 transition-colors">Privacidad</a>
            <a href="/terms" className="hover:text-slate-900 transition-colors">Términos</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
